import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, API_BASE } from "../lib/api";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Scale, CheckCircle2, XCircle, Percent, ExternalLink, AlertTriangle, CreditCard } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  ["open", "Ouverts"],
  ["escalated", "Escaladés"],
  ["resolved", "Résolus"],
];

const STATUS_LABELS = {
  OPEN: ["Ouvert", "bg-amber-100 text-amber-800"],
  ESCALATED: ["Escaladé — niveau 2", "bg-red-100 text-red-700"],
  RESOLVED_RELEASE: ["Résolu — fonds libérés", "bg-emerald-100 text-emerald-800"],
  RESOLVED_PARTIAL: ["Résolu — partiel", "bg-blue-100 text-blue-800"],
  RESOLVED_REFUND: ["Résolu — remboursé", "bg-blue-100 text-blue-800"],
};

const eur = (c) => `${((c || 0) / 100).toFixed(2)} €`;
const photoSrc = (url) => (url && url.startsWith("/api/") ? API_BASE + url.slice(4) : url);

function Thumbs({ photos }) {
  if (!photos?.length) return null;
  return (
    <div className="flex gap-1.5 flex-wrap mt-1.5">
      {photos.map(ph => (
        <a key={ph.index} href={photoSrc(ph.url)} target="_blank" rel="noreferrer" title="Ouvrir en grand"
           className="block w-14 h-14 rounded-md overflow-hidden border border-slate-200 hover:border-orange-400 transition-colors">
          <img src={photoSrc(ph.url)} alt="" loading="lazy" className="w-full h-full object-cover" />
        </a>
      ))}
    </div>
  );
}

function DisputeCard({ d, onResolve, busy }) {
  const [label, chip] = STATUS_LABELS[d.status] || [d.status, "bg-slate-100 text-slate-700"];
  const open = d.status === "OPEN" || d.status === "ESCALATED";
  const slaBreached = open && d.sla_deadline < new Date().toISOString();
  const p = d.payment;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`dispute-${d.dispute_id}`}>
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${chip}`}>{label}</span>
        {d.kind === "CHARGEBACK" && (
          <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 flex items-center gap-1">
            <CreditCard className="w-3 h-3" /> Chargeback Stripe
          </span>
        )}
        {slaBreached && (
          <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-600 text-white flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> SLA dépassé
          </span>
        )}
        <span className="text-xs text-slate-400 ml-auto">{d.dispute_id}</span>
      </div>

      <div className="font-display font-bold text-slate-900">
        {d.request?.title || d.mission_id}
        {d.request && (
          <span className="font-normal text-sm text-slate-500 ml-2">
            {d.request.pickup?.city} → {d.request.dropoff?.city}
          </span>
        )}
      </div>
      <div className="text-xs text-slate-500 mt-0.5">
        Client : <strong>{d.client?.name || "?"}</strong> · Dépanneur : <strong>{d.transporter?.name || "?"}</strong>
        {d.transporter?.company && <> ({d.transporter.company})</>}
      </div>

      <div className="mt-3 text-sm text-slate-700 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
        {d.reason}
      </div>

      {/* Photos contradictoires de la mission (chargement / livraison) */}
      {(d.photo_gates || []).length > 0 && (
        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          {d.photo_gates.map(g => (
            <div key={g.gate_id}>
              <div className="text-[11px] font-bold text-slate-500">
                {g.phase === "DEPART" ? "État au chargement" : "État à la livraison"}
              </div>
              <Thumbs photos={g.photos} />
            </div>
          ))}
        </div>
      )}

      {/* Pièces versées par les parties (dossier contradictoire) */}
      {(d.evidence || []).length > 0 && (
        <div className="mt-3 space-y-2">
          <div className="text-[11px] font-bold text-slate-500">Pièces versées au dossier</div>
          {d.evidence.map(ev => (
            <div key={ev.evidence_id} className={`border rounded-lg p-2.5 ${
              ev.by === "client" ? "border-blue-200 bg-blue-50/50"
              : ev.by === "transporter" ? "border-orange-200 bg-orange-50/50"
              : "border-slate-200 bg-slate-50"
            }`}>
              <div className="text-[11px] font-bold text-slate-700 mb-0.5">
                {ev.by === "client" ? "Client" : ev.by === "transporter" ? "Dépanneur" : "Médiation"}
                {ev.author_name ? ` — ${ev.author_name}` : ""} ·{" "}
                {new Date(ev.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
              </div>
              {ev.message && <div className="text-xs text-slate-700 whitespace-pre-wrap">{ev.message}</div>}
              <Thumbs photos={ev.photos} />
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 flex items-center gap-4 text-xs text-slate-500 flex-wrap">
        <span>Ouvert le {new Date(d.opened_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
        <span>SLA : {new Date(d.sla_deadline).toLocaleDateString("fr-FR")}</span>
        {p && <span>Séquestre : <strong>{eur(p.amount_total_cents)}</strong> · net dépanneur {eur(p.amount_transporter_cents)}</span>}
        {d.mission && (
          <Link to={`/requests/${d.mission.request_id}`} className="text-orange-600 hover:underline flex items-center gap-1">
            <ExternalLink className="w-3 h-3" /> Voir mission & photos
          </Link>
        )}
      </div>

      {d.resolution_note && (
        <div className="mt-2 text-xs text-slate-500">Note de résolution : {d.resolution_note}</div>
      )}
      {!open && (d.payout_cents > 0 || d.client_refund_cents > 0) && (
        <div className="mt-1 text-xs text-slate-600">
          {d.payout_cents > 0 && <>Reversé au dépanneur : <strong>{eur(d.payout_cents)}</strong> · </>}
          {d.client_refund_cents > 0 && <>Remboursé au client : <strong>{eur(d.client_refund_cents)}</strong></>}
        </div>
      )}

      {open && (
        <div className="mt-4 flex gap-2 flex-wrap">
          <Button size="sm" disabled={busy} onClick={() => onResolve(d, "RELEASE")}
                  className="bg-emerald-600 hover:bg-emerald-700 h-8">
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Libérer au dépanneur
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onResolve(d, "PARTIAL")}
                  className="h-8">
            <Percent className="w-3.5 h-3.5 mr-1" /> Déblocage partiel
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onResolve(d, "REFUND")}
                  className="h-8 text-red-600 border-red-200 hover:bg-red-50">
            <XCircle className="w-3.5 h-3.5 mr-1" /> Rembourser le client
          </Button>
        </div>
      )}
    </div>
  );
}

export default function AdminDisputes() {
  const [tab, setTab] = useState("open");
  const [disputes, setDisputes] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async (t = tab) => {
    try {
      const { data } = await api.get(`/admin/disputes?status=${t}`);
      setDisputes(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  useEffect(() => { load(tab); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolve = async (d, outcome) => {
    let transporter_cents = null;
    if (outcome === "PARTIAL") {
      const max = (d.payment?.amount_transporter_cents || 0) / 100;
      const raw = window.prompt(`Part reversée au dépanneur en € (0 à ${max.toFixed(2)}) — la commission reste acquise à la plateforme, le client récupère le reste :`);
      if (raw === null) return;
      const val = parseFloat(String(raw).replace(",", "."));
      if (isNaN(val) || val < 0 || val > max) { toast.error("Montant invalide."); return; }
      transporter_cents = Math.round(val * 100);
    }
    const note = window.prompt("Note de résolution (visible par les deux parties) :") || "";
    const confirmTxt = {
      RELEASE: "Libérer l'intégralité du net au dépanneur ?",
      PARTIAL: `Déblocage partiel : ${((transporter_cents || 0) / 100).toFixed(2)} € au dépanneur ?`,
      REFUND: "Rembourser intégralement le client (commission abandonnée) ?",
    }[outcome];
    if (!window.confirm(confirmTxt)) return;
    setBusy(true);
    try {
      await api.post(`/admin/disputes/${d.dispute_id}/resolve`, { outcome, transporter_cents, note });
      toast.success("Litige tranché");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-disputes-page">
      <Header />
      <div className="flex pt-16">
        <Sidebar />
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-4xl">
          <div className="flex items-center gap-2 mb-1">
            <Scale className="w-5 h-5 text-orange-600" />
            <div className="text-[10px] uppercase tracking-widest text-orange-600 font-bold">Administration</div>
          </div>
          <h1 className="font-display font-extrabold text-3xl text-slate-900 tracking-tight mb-1">Litiges</h1>
          <p className="text-sm text-slate-500 mb-4">
            Médiation sur photos contradictoires — SLA 7 jours ouvrés, gel sélectif par mission (CGU art. 7).
          </p>

          <div className="flex gap-1 mb-4">
            {TABS.map(([key, label]) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  tab === key ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {disputes.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-400 text-sm">
              Aucun litige {tab === "open" ? "ouvert" : tab === "escalated" ? "escaladé" : "résolu"}.
            </div>
          ) : (
            <div className="space-y-3">
              {disputes.map(d => (
                <DisputeCard key={d.dispute_id} d={d} onResolve={resolve} busy={busy} />
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
